import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import type { MeResponse } from '@shared/types';
import { api, ApiError, apiUrl } from '@/lib/api';
import { clearSession, detailsGiven, loadMe, loadSession, markDetailsGiven, ownSession, resultPath, type Session } from '@/lib/session';
import { isSeatMoved } from '@/lib/seat';
import { useProof } from '@/lib/useProof';
import { ACCOUNT_OFFERS } from '@/lib/proof';
import type { ResumeState } from './parts/moments';
import { useStaffSession } from '@/staff/session';
import { useGame } from '@/store/game';
import { Button, buttonClass } from '@/components/Button';
import { ProofMark } from '@/components/ProofMark';
import { Wordmark } from '@/brand/Wordmark';
import { YEARS } from '@/data/medSchools';
import { ShareCard } from '@/share/ShareCard';
import { copyText } from '@/share/share';
import type { ShareFacts } from '@/share/card';
import { Screen } from './parts/Screen';
import { PlaceLine, StatGrid } from './parts/StatGrid';
import { formatName } from './parts/copy';
import { FINISH, finishFacts, finishLine, finishOf, finishStats, type ResultArrival, type SavedKind } from './parts/finish';
import { DetailsForm, EMPTY_DETAILS, detailsError, saveDetails, useDrawWindow, withSaved, type DetailsValues } from './parts/DetailsForm';
import { prizePhrase, termsPath } from './parts/prize';
import { reviewLines, type ReviewItem, type Verdict } from './parts/review';
import { useBattleLook } from '@/look';
import { StemText } from '@/components/Stem';
import { NEXT, NextBattle } from './parts/NextBattle';
import { QUALIFY, qualifies, topWithoutPoints } from './parts/qualify';
import { countShare, inviteUrl } from '@/share/share';

const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/\.?$/, '.') : s);
const VERDICT: Record<Verdict, { cls: string; label: string }> = {
  right: { cls: 'text-correct-strong', label: 'Right' }, partial: { cls: 'text-[var(--amber-strong)]', label: 'Partly right' },
  wrong: { cls: 'text-wrong-strong', label: 'Wrong' }, none: { cls: 'text-ink-3', label: 'Not answered' },
};

/**
 * M11: your result. The figures, the end-of-battle details, the story card and, once the battle is over, every answer
 * with the reason. Each fact once: the line lives on the card. 9 Oct: the review is never locked: it opens for everyone.
 * The details were asked for on the step before this page (screens/Details.tsx); a player who skipped it, or came by a
 * link, sees the battle's offer once more as a card whose button opens the form here; once saved, one line says so, with
 * a small link to change them. R1 (5 Oct review): the link it copies, and the address it shows, carry the read-only
 * token from GET /me, never the session. Opened from such a link, the form saves with this device's session for that
 * player, or asks for the email they joined with.
 */
export default function Result() {
  const { token: tokenParam } = useParams(); const held = useGame((s) => s.session); const nav = useNavigate();
  // 9 Oct: arriving from the details step after a save, with what was saved (the draw note, if any).
  const arrival = (useLocation().state ?? undefined) as ResultArrival | undefined;
  // The address's token (a result link's, or an older link's session), else this device's session.
  const token = tokenParam ?? held?.token ?? loadSession()?.token;
  // 9 Oct: another device took this seat (lib/seat: the session refused with seat-moved by /me, the claim form or the
  // proof). The session is forgotten and the battle's join page says so; with no session for a battle to go back to
  // (an older link's token in the address), the refusal is shown here instead. True when the page has gone.
  const seatGone = (): boolean => {
    const s = held ?? loadSession(); clearSession(); const g = useGame.getState(); g.setSession(undefined); g.clearSeatMoved();
    if (!s) return false;
    nav(`/j/${s.code}`, { replace: true, state: { resume: 'moved', name: s.name } satisfies ResumeState });
    return true;
  };
  const moved = useGame((s) => s.seatMoved);
  useEffect(() => { if (moved) seatGone(); }, [moved]); // eslint-disable-line react-hooks/exhaustive-deps
  const [me, setMe] = useState<MeResponse>(); const [items, setItems] = useState<ReviewItem[]>(); const [err, setErr] = useState<string>(); const [copied, setCopied] = useState(false);
  // 9 Oct: the /me answered with this device's own session (the only one that carries what offers proof of the address).
  const [ownMe, setOwnMe] = useState<MeResponse>();
  // The draw starts unticked: the prize terms say players choose to enter.
  const [form, setForm] = useState<DetailsValues>(EMPTY_DETAILS); const [email, setEmail] = useState('');
  // The form opens inline: from the offer card (details not given yet) or the change link (details saved).
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<SavedKind>('no'); const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr(FINISH.none); return; }
    let live = true;
    api<MeResponse>('/me', { token }).then((m) => {
      if (!live) return;
      setMe(m); setSaved(m.profileDone ? arrival?.saved ?? 'before' : 'no');
      if (!m.readOnly) setOwnMe(m);
      // Saved details (asked with the session only) fill the form, so updating or confirming them is a tap.
      setForm((f) => withSaved(f, m.details));
      // Opened from the result link (read-only, so no saved details) on the device that joined: that device's session fetches them.
      const mine = m.readOnly ? ownSession(m.state.code, m.playerId, held) : undefined;
      if (mine) api<MeResponse>('/me', { token: mine.token }).then((x) => { if (!live || !x) return; setOwnMe(x); setForm((f) => withSaved(f, x.details)); }).catch(() => {});
      // Opened with the session in the address: show the read-only link there instead, so copying the address shares only the result.
      if (tokenParam && !m.readOnly && m.resultToken) window.history.replaceState(window.history.state, '', resultPath(m.resultToken));
      const code = m.state.code;
      if (m.state.status === 'podium' || m.state.status === 'ended') {
        api<{ items: ReviewItem[] }>(`/battles/${code}/review`, { token }).then((r) => { if (live && Array.isArray(r?.items)) setItems(r.items); }).catch(() => {});
      }
    }).catch((e) => {
      if (!live) return;
      if (isSeatMoved(e) && seatGone()) return;
      setErr(e instanceof ApiError ? sentence(e.message) : FINISH.failed);
    });
    return () => { live = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // 9 Oct: the session that can prove this seat's address: the page's own (its token is the session), or, from a result
  // link, the one this device holds for the player. The proof hook and the person icon work from it and from ownMe.
  const proofWith = useMemo<Session | undefined>(() => {
    if (!me) return undefined;
    if (me.readOnly) return ownSession(me.state.code, me.playerId, held);
    return token ? { code: me.state.code, token, playerId: me.playerId, name: me.name } : undefined;
  }, [me, held, token]);
  useProof({ session: proofWith, me: ownMe, onProved: (m) => {
    setOwnMe(m);
    setForm((f) => withSaved(f, m.details));
    if (!me?.readOnly) setMe(m);
  } });

  useBattleLook(me?.state);
  // When the draw closes: claimHours after the battle ended. The server has the last word when the form is saved.
  const drawWindow = useDrawWindow(me?.state);
  const { prize, draw, closed } = drawWindow;
  // A result link's token only reads: the form saves with this device's session for the player, else it needs their email.
  const readOnly = me?.readOnly === true;
  const own = me && readOnly ? ownSession(me.state.code, me.playerId, held) : undefined;
  const needEmail = readOnly && !own;
  const linkToken = me?.resultToken ?? (readOnly ? token : undefined);

  /** 13 Oct: where a shared link takes a friend: the open battles, tagged with where the link came from (?ref=, see lib/session). */
  function invite(from: 'share' | 'result', path = false) {
    const to = `/open?ref=${from}-${me!.state.code}`;
    return path ? to : `${window.location.origin}${to}`;
  }
  async function copy() {
    if (!linkToken) return;
    const url = `${window.location.origin}${resultPath(linkToken)}`;
    if (await copyText(url)) setCopied(true); else window.prompt(FINISH.copyPage, url);
  }
  async function save() {
    const by = own?.token ?? token; if (!by) return;
    setBusy(true); setErr(undefined);
    try {
      setSaved(await saveDetails(by, form, { draw: drawWindow, ...(needEmail ? { email } : {}) })); setEditing(false);
      markDetailsGiven(needEmail ? email : loadMe()?.email);
    } catch (e) {
      if (isSeatMoved(e) && seatGone()) return;
      setErr(detailsError(e));
    } finally { setBusy(false); }
  }

  // 8 Oct: an account keeps the result. Signed in: the way to every battle; otherwise registration with the address this device joined with.
  const account = useStaffSession();
  if (err && !me) return <Screen center><p role="alert" className="rounded-tile bg-wrong-soft px-4 py-3 text-ink">{err}</p></Screen>;
  if (!me) return <Screen center><p className="text-center font-semibold text-ink-2">{FINISH.loading}</p></Screen>;
  const f = finishFacts(me, me.state.top);
  const line = finishLine(f);
  const tiles = finishStats(f);
  // Something to share: answers that scored. Otherwise one line stands in for the tiles and there is no card.
  const scored = tiles.length > 0;
  const over = me.state.status === 'podium' || me.state.status === 'ended';
  const facts: ShareFacts = { title: me.state.title, name: me.name, rank: f.rank, players: f.players, score: f.score, accuracy: f.accuracy, bestStreak: f.bestStreak, line };
  // The battle's form and trial offer (always on /me since 9 Oct).
  const finish = finishOf(me);
  const asks = finish.askDetails;
  // M1: what this device knows about the seat: its own session's /me when it has one (a result link opened on the device
  // that joined, or after a proof there), else the page's. The record and the saved state come from it.
  const seat = ownMe ?? me;
  // 14 Oct: details given on this phone for this address today count as given here too (lib/session detailsGiven).
  const savedNow: SavedKind = saved !== 'no' ? saved : seat.profileDone || (!needEmail && detailsGiven(loadMe()?.email)) ? 'before' : 'no';
  const done = savedNow !== 'no';
  // 9 Oct: the record comes only once the address is proved; `known` on its own (an unproved address that has played) is no welcome.
  const record = seat.returning && typeof seat.returning.battles === 'number' ? { battles: seat.returning.battles, best: seat.returning.best, yearStale: seat.returning.yearStale === true } : undefined;
  const back = asks && record && savedNow === 'before' && !editing ? record : undefined;
  // One line once the details are saved: the battle's thank-you, and the draw entry when there was one.
  const drawNote = savedNow === 'draw' ? FINISH.drawIn : savedNow === 'closed' ? FINISH.drawClosed : '';
  const status = asks && done && !editing ? [finish.thanks, drawNote].filter(Boolean).join(' ') : undefined;
  const field = 'flex flex-col gap-1.5';
  const label = 'text-sm font-semibold text-ink-2';
  // The battle's heading and message, at the top of the offer card and of the form opened from it.
  const offerHead = (
    <div className="flex flex-col gap-1">
      <h2 id="finish-heading" className="display text-2xl text-ink">{finish.heading}</h2>
      <p className="text-ink-2">{finish.message}</p>
      {editing && !needEmail && <p className="text-sm text-ink-3">{FINISH.emailNote}</p>}
    </div>
  );
  const changeLink = (
    <button type="button" data-part="change-details" onClick={() => setEditing(true)}
      className="inline-flex min-h-11 w-fit items-center text-sm font-semibold text-iv-strong underline underline-offset-2">{FINISH.change}</button>
  );

  return (
    <Screen className="gap-6 pb-12">
      <header className="flex flex-col gap-4 pt-4">
        {/* One Pulse per screen: the story card draws its own, so the wordmark's line shows only when there is no card. */}
        <div className="flex items-center justify-between gap-3">
          <Wordmark variant="compact" pulse={!scored} />
          <ProofMark where="result" me={ownMe} session={proofWith} />
        </div>
        <div className="flex flex-col gap-1">
          {me.state.title?.trim() && <p className="font-semibold text-ink-2">{me.state.title}</p>}
          <h1 className="display text-[2.6rem] text-ink">{FINISH.title(me.name)}</h1>
        </div>
      </header>

      <div data-part="result-figures" className="flex flex-col gap-3">
        {f.rank !== undefined && <PlaceLine rank={f.rank} players={f.players} />}
        {scored ? <StatGrid items={tiles} /> : line && <p className="text-lg font-semibold leading-snug text-brand-strong">{line}</p>}
      </div>

      {/* 13 Oct: a result link sent on to a friend is an invitation: a device with no session for this player gets the way in. */}
      {needEmail && <Link to={invite('result', true)} data-part="invite" className={buttonClass({ className: 'w-full' })}>{FINISH.playNext}</Link>}

      {/* 14 Oct: a place in the battle's top N qualifies for the Grand Final; 16 Oct: with at least one point (@shared/qualify). */}
      {over && me.state.qualify && qualifies(me.state.qualify, f.rank, f.score) && (
        <section aria-labelledby="qualified-heading" data-part="qualified" className="flex flex-col gap-1 rounded-card bg-surface p-5 shadow-card">
          <h2 id="qualified-heading" className="display text-2xl text-ink">{QUALIFY.yes(me.state.qualify)}</h2>
          <p className="text-ink-2">{QUALIFY.yesLine(f.rank!)}</p>
        </section>
      )}
      {over && me.state.qualify && topWithoutPoints(me.state.qualify, f.rank, f.score) && (
        <p data-part="qualify-points" className="font-semibold text-brand-strong">{QUALIFY.needsPoint(me.state.qualify)}</p>
      )}

      {/* 14 Oct: joined through a friend's link: the score to beat, and how it went. */}
      {over && seat.challenge && (
        <section aria-labelledby="challenge-heading" data-part="challenge" className="flex flex-col gap-1 rounded-card bg-surface p-5 shadow-card">
          <h2 id="challenge-heading" className="display text-2xl text-ink">{FINISH.challengeTitle(seat.challenge.name)}</h2>
          <p className="numeral text-lg text-ink">{`You ${f.score.toLocaleString('en-GB')} · ${seat.challenge.name} ${seat.challenge.score.toLocaleString('en-GB')}`}</p>
          <p className="text-ink-2">{f.score > seat.challenge.score ? FINISH.challengeWon(seat.challenge.name, f.score - seat.challenge.score)
            : f.score < seat.challenge.score ? FINISH.challengeLost(seat.challenge.name, seat.challenge.score - f.score) : FINISH.challengeTied(seat.challenge.name)}</p>
        </section>
      )}

      {/* 14 Oct: battles run back to back: the next one, one tap from the join card with the remembered name. */}
      {over && <NextBattle except={me.state.code} heading={() => (me.state.qualify && !qualifies(me.state.qualify, f.rank, f.score) ? QUALIFY.another(me.state.qualify) : NEXT.next)} cta={NEXT.joinNext} source={`next-${me.state.code}`} />}

      {over && (account ? (
        <p><Link to="/home" className="font-semibold text-iv-strong underline underline-offset-2">{FINISH.myBattles}</Link></p>
      ) : ACCOUNT_OFFERS.on && (
        <section aria-labelledby="keep-heading" data-part="keep-offer" className="flex flex-col gap-2 rounded-card bg-surface p-5 shadow-card">
          <h2 id="keep-heading" className="display text-2xl text-ink">{FINISH.keepTitle}</h2>
          <p className="text-ink-2">{FINISH.keepLine}</p>
          <Link to={`/register?${new URLSearchParams({ ...(loadMe()?.email ? { email: loadMe()!.email.trim().toLowerCase() } : {}), from: 'result', next: '/home' }).toString()}`}
            className="inline-flex min-h-11 w-fit items-center font-semibold text-iv-strong underline underline-offset-2">{FINISH.keep}</Link>
        </section>
      ))}

      {asks && editing ? (
        <DetailsForm labelledBy="finish-heading" head={offerHead} values={form} onChange={setForm}
          needEmail={needEmail} email={email} onEmail={setEmail} draw={drawWindow} code={me.state.code}
          err={err} busy={busy} submitLabel={finish.button} onSubmit={() => void save()} onCancel={() => { setEditing(false); setErr(undefined); }} />
      ) : back ? (
        <section aria-labelledby="back-title" data-part="welcome-back" className="flex flex-col gap-3 rounded-card bg-surface p-5 shadow-card">
          <p id="back-title" className="font-semibold text-brand-strong">{FINISH.welcome(form.firstName || me.name)}</p>
          <h2 className="display text-2xl text-ink">{finish.heading}</h2>
          {FINISH.history(back.battles, back.best) && <p className="text-ink-2">{FINISH.history(back.battles, back.best)}</p>}
          <p className="text-ink-2">{FINISH.savedLast}</p>
          {back.yearStale && (
            <div className="flex flex-col gap-2 rounded-tile bg-surface-2 px-3.5 py-3">
              <label className={field}><span className={label}>{FINISH.yearCheck(form.year || '–')}</span>
                <select id="yearCheck" className="rd-field" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })}>
                  {YEARS.map((s) => <option key={s}>{s}</option>)}
                </select></label>
              <Button variant="quiet" disabled={busy} busy={busy} onClick={() => void save()}>{FINISH.yearSave}</Button>
            </div>
          )}
          {draw && prize && !closed && (
            <div className="flex flex-col gap-2 rounded-tile bg-surface-2 px-3.5 py-3">
              <label className="flex min-h-11 items-start gap-3 text-[15px] leading-snug text-ink">
                <input id="drawOptInBack" type="checkbox" className="mt-0.5 size-5 shrink-0 accent-brand" checked={form.drawOptIn} onChange={(e) => setForm({ ...form, drawOptIn: e.target.checked })} />
                <span>{FINISH.draw(prizePhrase(prize.prize))}</span>
              </label>
              <p className="pl-8 text-sm text-ink-2">{FINISH.agreeBefore}<a href={termsPath(me.state.code)} target="_blank" rel="noopener" className="py-3 font-semibold text-iv-strong underline underline-offset-2">{FINISH.agreeLink}</a>{FINISH.agreeAfter}</p>
              <Button disabled={busy || !form.drawOptIn} busy={busy} onClick={() => void save()}>{FINISH.enterDraw}</Button>
            </div>
          )}
          {err && <p role="alert" className="rounded-tile bg-wrong-soft px-4 py-3 text-[15px] text-ink">{err}</p>}
          {changeLink}
        </section>
      ) : status ? (
        <div data-part="saved" className="flex flex-col gap-1">
          <p role="status" data-part="saved-status" className="font-semibold leading-snug text-ink">{status}</p>
          {changeLink}
        </div>
      ) : asks && !done && (
        // Not given yet (the step skipped, or a link opened elsewhere): the offer once more, never a gate.
        <section aria-labelledby="finish-heading" data-part="details-offer" className="flex flex-col gap-4 rounded-card bg-surface p-5 shadow-card">
          {offerHead}
          <Button onClick={() => setEditing(true)}>{finish.button}</Button>
        </section>
      )}

      {scored && <ShareCard facts={facts} url={proofWith ? inviteUrl(me.state.code, me.playerId) : invite('share')} onShared={() => countShare(proofWith?.token, apiUrl)}
        note={seat.sharing && seat.sharing.joins > 0 ? FINISH.friendsJoined(seat.sharing.joins) : undefined} />}

      {!over && <p className="text-ink-2">{FINISH.reviewLater}</p>}
      {items && (
        <section className="flex flex-col gap-3" aria-labelledby="review-title">
          <h2 id="review-title" className="display text-3xl text-ink">{FINISH.answers}</h2>
          {items.map((it) => {
            const r = reviewLines(it, me.answers[`q${it.index}`]); const v = VERDICT[r.verdict];
            return (
              <article key={it.index} className="flex flex-col gap-2 rounded-tile border border-line bg-surface p-4">
                <p className="flex items-center justify-between gap-3 text-sm font-semibold text-ink-3">
                  <span>{FINISH.question(it.index)}{it.format ? ` · ${formatName({ format: it.format, graphic: it.graphic, multi: it.multi?.mode ? { mode: it.multi.mode } : undefined, hotspot: it.hotspot?.target ? { target: it.hotspot.target } : undefined })}` : ''}</span>
                  <span className={v.cls}>{r.points}</span>
                </p>
                <p className="font-semibold leading-snug text-ink"><StemText text={it.stem} /></p>
                {/* Not answered: the points slot already says so. */}
                {r.verdict !== 'none' && (
                  <p className={`flex items-start gap-2 text-sm ${r.verdict === 'right' ? 'text-correct-strong' : 'text-ink-2'}`}>
                    {r.verdict === 'right' || r.verdict === 'partial'
                      ? <Check aria-label={v.label} className={`mt-0.5 size-4 shrink-0 ${v.cls}`} strokeWidth={3} />
                      : <X aria-label={v.label} className={`mt-0.5 size-4 shrink-0 ${v.cls}`} strokeWidth={3} />}
                    <span>{FINISH.mine}: {r.mine ?? '–'}</span>
                  </p>
                )}
                {r.verdict !== 'right' && r.right && <p className="flex items-start gap-2 text-sm text-correct-strong"><Check aria-hidden="true" className="mt-0.5 size-4 shrink-0" strokeWidth={3} /><span>{FINISH.right}: {r.right}</span></p>}
                <p className="text-sm leading-relaxed text-ink-2">{it.explanation}</p>
              </article>
            );
          })}
        </section>
      )}

      {linkToken && <Button variant="quiet" onClick={() => void copy()}>{copied ? FINISH.copied : FINISH.copyPage}</Button>}
    </Screen>
  );
}
